import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

export default function CarpenterDashboard() {
  const navigate = useNavigate();

  const [carpenterName, setCarpenterName] = useState("Carpenter");
  const [stats, setStats]   = useState({ labours: 0, present: 0, pending: 0 });
  const [labours, setLabours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [time, setTime] = useState(new Date());

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, laboursRes] = await Promise.all([
          api.get("/carpenter/stats"),
          api.get("/carpenter/labours"),
        ]);
        if (statsRes.data.name) setCarpenterName(statsRes.data.name.split(" ")[0]);
        setStats({
          labours: statsRes.data.totalLabours   ?? 0,
          present: statsRes.data.presentToday   ?? 0,
          pending: statsRes.data.pendingPayments ?? 0,
        });
        setLabours(laboursRes.data.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const greeting = () => {
    const h = time.getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const navItems = [
    { label: "Dashboard",        emoji: "🏠", path: "/carpenter/dashboard",         active: true },
    { label: "My Labours",       emoji: "👷", path: "/carpenter/labours" },
    { label: "Add Labour",       emoji: "➕", path: "/carpenter/add-labour" },
    { label: "Attendance",       emoji: "📋", path: "/carpenter/attendance" },
    { label: "Payments",         emoji: "💰", path: "/carpenter/payments" },
    { label: "Salary",           emoji: "💵", path: "/carpenter/salary" },
    { label: "Inactive Labours", emoji: "♻️", path: "/carpenter/inactive-labours" },
  ];

  const statCards = [
    {
      label: "My Labours",
      value: stats.labours,
      emoji: "👷",
      color: "#D97706",
      bg: "#FEF3C7",
      border: "#FDE68A",
      desc: "Total labour accounts",
      path: "/carpenter/labours",
    },
    {
      label: "Present Today",
      value: stats.present,
      emoji: "✅",
      color: "#059669",
      bg: "#D1FAE5",
      border: "#A7F3D0",
      desc: "Marked attendance today",
      path: "/carpenter/attendance",
    },
    {
      label: "Pending Payments",
      value: stats.pending,
      emoji: "⏳",
      color: "#DC2626",
      bg: "#FEF2F2",
      border: "#FECACA",
      desc: "Dues to be cleared",
      path: "/carpenter/payments",
    },
  ];

  const quickActions = [
    {
      label: "Add Labour",
      emoji: "➕",
      desc: "Create a new labour account",
      color: "#D97706",
      bg: "linear-gradient(135deg,#FEF3C7,#FDE68A)",
      border: "#FDE68A",
      path: "/carpenter/add-labour",
      primary: true,
    },
    {
      label: "Mark Attendance",
      emoji: "📋",
      desc: "Record today's attendance",
      color: "#0369A1",
      bg: "linear-gradient(135deg,#E0F2FE,#BAE6FD)",
      border: "#BAE6FD",
      path: "/carpenter/attendance",
    },
    {
      label: "Make Payment",
      emoji: "💸",
      desc: "Pay salary or advance",
      color: "#059669",
      bg: "linear-gradient(135deg,#D1FAE5,#A7F3D0)",
      border: "#A7F3D0",
      path: "/carpenter/payments",
    },
    {
      label: "View Salary",
      emoji: "💵",
      desc: "Salary records & slips",
      color: "#7C3AED",
      bg: "linear-gradient(135deg,#EDE9FE,#DDD6FE)",
      border: "#DDD6FE",
      path: "/carpenter/salary",
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        @keyframes fadeUp   { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin     { to { transform:rotate(360deg); } }
        @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:.5} }
        @keyframes slideIn  { from { transform:translateX(-100%); } to { transform:translateX(0); } }

        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        body { font-family:'Plus Jakarta Sans',sans-serif; background:#FAF7F4; }

        /* ── SIDEBAR ── */
        .sidebar {
          width:220px; flex-shrink:0;
          background:#fff; border-right:1.5px solid #E7E5E4;
          padding:24px 14px; display:flex; flex-direction:column; gap:6px;
          position:sticky; top:0; height:100vh; overflow-y:auto;
        }
        .topbar { display:none; }
        .hamburger { display:none; align-items:center; justify-content:center; width:40px; height:40px; border-radius:10px; border:1.5px solid #E7E5E4; background:#fff; cursor:pointer; flex-shrink:0; }
        .sidebar-overlay { display:none; position:fixed; inset:0; background:rgba(0,0,0,0.4); z-index:40; }

        .nav-btn { display:flex; align-items:center; gap:9px; width:100%; padding:10px 12px; border-radius:10px; border:none; background:none; font-family:'Plus Jakarta Sans',sans-serif; font-size:14px; font-weight:600; color:#78716C; cursor:pointer; transition:background 0.15s,color 0.15s; text-align:left; }
        .nav-btn:hover, .nav-btn.active { background:#FEF3C7; color:#D97706; }
        .nav-btn.danger:hover { background:#FEF2F2; color:#DC2626; }

        /* ── STAT CARDS ── */
        .stat-card {
          background:#fff; border:1.5px solid #E7E5E4; border-radius:16px;
          padding:20px; cursor:pointer; position:relative; overflow:hidden;
          animation:fadeUp 0.4s ease both;
          transition:transform 0.2s, box-shadow 0.2s, border-color 0.2s;
        }
        .stat-card:hover { transform:translateY(-3px); box-shadow:0 12px 32px rgba(0,0,0,0.09); border-color:#D97706; }

        /* ── QUICK ACTION CARDS ── */
        .qa-card {
          border-radius:14px; padding:18px; cursor:pointer;
          border:1.5px solid transparent;
          animation:fadeUp 0.4s ease both;
          transition:transform 0.18s, box-shadow 0.18s;
          display:flex; align-items:flex-start; gap:14px;
        }
        .qa-card:hover { transform:translateY(-2px); box-shadow:0 10px 28px rgba(0,0,0,0.1); }

        /* ── HERO BANNER ── */
        .hero-banner {
          border-radius:20px; padding:28px 32px;
          background:linear-gradient(135deg,#92400E 0%,#B45309 45%,#D97706 100%);
          position:relative; overflow:hidden;
          animation:fadeUp 0.35s ease both;
        }
        .hero-banner::before {
          content:'🪚';
          position:absolute; right:24px; top:50%; transform:translateY(-50%);
          font-size:80px; opacity:0.12; pointer-events:none;
        }

        /* ── LIVE BADGE ── */
        .live-dot { width:7px; height:7px; border-radius:50%; background:#22C55E; animation:pulse 1.5s ease infinite; display:inline-block; }

        /* ── RESPONSIVE ── */
        @media (max-width:768px) {
          .sidebar { position:fixed; left:0; top:0; z-index:50; transform:translateX(-100%); transition:transform 0.28s ease; height:100%; }
          .sidebar.open { transform:translateX(0); }
          .sidebar-overlay.open { display:block; }
          .topbar { display:flex; align-items:center; gap:12px; padding:14px 16px; background:#fff; border-bottom:1.5px solid #E7E5E4; position:sticky; top:0; z-index:30; }
          .hamburger { display:flex; }
          .main-wrap { flex-direction:column !important; }
          .main-content { padding:16px !important; }
          .stats-grid { grid-template-columns:1fr 1fr !important; gap:12px !important; }
          .qa-grid { grid-template-columns:1fr 1fr !important; gap:10px !important; }
          .hero-banner { padding:20px !important; }
          .hero-banner::before { font-size:50px; right:12px; }
        }
        @media (max-width:420px) {
          .stats-grid { grid-template-columns:1fr !important; }
          .qa-grid    { grid-template-columns:1fr !important; }
        }
        @media (min-width:769px) and (max-width:1100px) {
          .sidebar { width:190px; }
        }
      `}</style>

      <div className="main-wrap" style={{ display:"flex", minHeight:"100vh", fontFamily:"'Plus Jakarta Sans',sans-serif", backgroundColor:"#FAF7F4" }}>

        {/* OVERLAY */}
        <div className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`} onClick={() => setSidebarOpen(false)} />

        {/* ── SIDEBAR ── */}
        <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
          {/* Brand */}
          <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"28px", padding:"0 4px" }}>
            <div style={{ width:"36px", height:"36px", background:"linear-gradient(135deg,#92400E,#D97706)", borderRadius:"10px", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <span style={{ fontSize:"18px" }}>🪚</span>
            </div>
            <div>
              <p style={{ fontWeight:"800", fontSize:"15px", color:"#1C1917", letterSpacing:"-0.3px" }}>Craftigo</p>
              <p style={{ fontSize:"11px", color:"#A8A29E", fontWeight:"500" }}>Carpenter Panel</p>
            </div>
          </div>

          <p style={{ fontSize:"11px", fontWeight:"700", color:"#C4B5A4", letterSpacing:"0.7px", textTransform:"uppercase", padding:"0 6px", marginBottom:"6px" }}>Menu</p>

          {navItems.map((n) => (
            <button
              key={n.label}
              className={`nav-btn ${n.active ? "active" : ""}`}
              onClick={() => { navigate(n.path); setSidebarOpen(false); }}
            >
              {n.emoji} {n.label}
            </button>
          ))}

          <div style={{ flex:1 }} />

          {/* Carpenter badge */}
          <div style={{ padding:"12px", background:"linear-gradient(135deg,#FEF3C7,#FFF7ED)", borderRadius:"12px", border:"1px solid #FDE68A", marginBottom:"10px" }}>
            <p style={{ fontSize:"12px", fontWeight:"700", color:"#92400E" }}>🪚 Carpenter</p>
            <p style={{ fontSize:"11px", color:"#B45309", marginTop:"2px" }}>Manage your team</p>
          </div>

          <button className="nav-btn danger" onClick={() => { localStorage.clear(); navigate("/login"); }}>
            🚪 Logout
          </button>
        </aside>

        {/* ── RIGHT SIDE ── */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>

          {/* TOPBAR (mobile) */}
          <div className="topbar">
            <button className="hamburger" onClick={() => setSidebarOpen(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <line x1="3" y1="6"  x2="21" y2="6"  stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="12" x2="21" y2="12" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="18" x2="21" y2="18" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <span style={{ fontWeight:"800", fontSize:"16px", color:"#1C1917" }}>🪚 Craftigo</span>
            <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:"6px" }}>
              <span className="live-dot" />
              <span style={{ fontSize:"11px", fontWeight:"600", color:"#059669" }}>Live</span>
            </div>
          </div>

          {/* MAIN */}
          <main className="main-content" style={{ padding:"28px 32px", flex:1, overflowY:"auto" }}>

            {/* ── HERO BANNER ── */}
            <div className="hero-banner" style={{ marginBottom:"24px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"16px" }}>
                <span className="live-dot" />
                <span style={{ fontSize:"11px", fontWeight:"700", color:"rgba(255,255,255,0.7)", letterSpacing:"0.6px", textTransform:"uppercase" }}>
                  {time.toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" })}
                </span>
              </div>
              <p style={{ fontSize:"13px", color:"rgba(255,255,255,0.7)", fontWeight:"500", marginBottom:"6px" }}>
                {greeting()} 👋
              </p>
              <h1 style={{ fontSize:"26px", fontWeight:"800", color:"#fff", letterSpacing:"-0.5px", marginBottom:"18px" }}>
                Welcome back, {carpenterName}!
              </h1>
              <button
                onClick={() => navigate("/carpenter/add-labour")}
                style={{ padding:"10px 20px", background:"#fff", border:"none", borderRadius:"10px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"13px", fontWeight:"700", color:"#D97706", cursor:"pointer", display:"inline-flex", alignItems:"center", gap:"7px", boxShadow:"0 4px 14px rgba(0,0,0,0.15)", transition:"transform 0.15s" }}
                onMouseEnter={(e) => e.currentTarget.style.transform="translateY(-1px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform="translateY(0)"}
              >
                ➕ Add New Labour
              </button>
            </div>

            {/* ── STAT CARDS ── */}
            <p style={{ fontSize:"11px", fontWeight:"700", color:"#A8A29E", letterSpacing:"0.8px", textTransform:"uppercase", marginBottom:"12px" }}>
              Today's Overview
            </p>
            <div className="stats-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"14px", marginBottom:"24px" }}>
              {statCards.map((c, i) => (
                <div
                  key={c.label}
                  className="stat-card"
                  style={{ animationDelay:`${i * 0.07}s` }}
                  onClick={() => navigate(c.path)}
                >
                  <div style={{ position:"absolute", top:0, left:0, right:0, height:"3px", background:c.color, borderRadius:"16px 16px 0 0" }} />
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"14px" }}>
                    <div style={{ width:"44px", height:"44px", borderRadius:"12px", background:c.bg, border:`1.5px solid ${c.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"22px" }}>
                      {c.emoji}
                    </div>
                    <span style={{ fontSize:"28px", fontWeight:"800", color:c.color }}>
                      {loading ? (
                        <span style={{ display:"inline-block", width:"28px", height:"28px", border:`3px solid ${c.border}`, borderTop:`3px solid ${c.color}`, borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
                      ) : c.value}
                    </span>
                  </div>
                  <p style={{ fontSize:"14px", fontWeight:"700", color:"#1C1917", marginBottom:"3px" }}>{c.label}</p>
                  <p style={{ fontSize:"12px", color:"#A8A29E", fontWeight:"500" }}>{c.desc}</p>
                </div>
              ))}
            </div>

            {/* ── QUICK ACTIONS ── */}
            <p style={{ fontSize:"11px", fontWeight:"700", color:"#A8A29E", letterSpacing:"0.8px", textTransform:"uppercase", marginBottom:"12px" }}>
              Quick Actions
            </p>
            <div className="qa-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:"12px", marginBottom:"24px" }}>
              {quickActions.map((q, i) => (
                <div
                  key={q.label}
                  className="qa-card"
                  style={{ background:q.bg, borderColor:q.border, animationDelay:`${0.21 + i * 0.07}s`, flexDirection:"column", gap:"10px" }}
                  onClick={() => navigate(q.path)}
                >
                  <div style={{ width:"42px", height:"42px", borderRadius:"12px", background:"rgba(255,255,255,0.7)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"22px", flexShrink:0 }}>
                    {q.emoji}
                  </div>
                  <div>
                    <p style={{ fontSize:"13px", fontWeight:"800", color:"#1C1917", marginBottom:"3px" }}>{q.label}</p>
                    <p style={{ fontSize:"11px", color:"#78716C", fontWeight:"500", lineHeight:"1.4" }}>{q.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ── RECENT ACTIVITY — Real Labours ── */}
            <div style={{ background:"#fff", border:"1.5px solid #E7E5E4", borderRadius:"16px", padding:"20px", animation:"fadeUp 0.4s 0.35s ease both" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"16px" }}>
                <div>
                  <p style={{ fontSize:"11px", fontWeight:"700", color:"#A8A29E", letterSpacing:"0.7px", textTransform:"uppercase", marginBottom:"4px" }}>Your Team</p>
                  <h3 style={{ fontSize:"16px", fontWeight:"800", color:"#1C1917" }}>Labour Overview</h3>
                </div>
                <button
                  onClick={() => navigate("/carpenter/labours")}
                  style={{ padding:"8px 14px", background:"#FEF3C7", border:"1px solid #FDE68A", borderRadius:"10px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"12px", fontWeight:"700", color:"#D97706", cursor:"pointer" }}
                >
                  View All →
                </button>
              </div>

              {loading ? (
                <div style={{ padding:"32px", textAlign:"center" }}>
                  <div style={{ width:"28px", height:"28px", border:"3px solid #FDE68A", borderTop:"3px solid #D97706", borderRadius:"50%", animation:"spin 0.7s linear infinite", margin:"0 auto 10px" }} />
                  <p style={{ color:"#A8A29E", fontSize:"13px" }}>Loading team…</p>
                </div>
              ) : labours.length === 0 ? (
                /* ── True empty state — no labours at all ── */
                <div style={{ padding:"28px", textAlign:"center", background:"#FFFBF7", borderRadius:"12px", border:"1px dashed #E7E5E4" }}>
                  <p style={{ fontSize:"28px", marginBottom:"8px" }}>👷</p>
                  <p style={{ fontSize:"14px", fontWeight:"700", color:"#78716C", marginBottom:"4px" }}>No labours yet</p>
                  <p style={{ fontSize:"12px", color:"#A8A29E" }}>Add your first labour to get started</p>
                  <button
                    onClick={() => navigate("/carpenter/add-labour")}
                    style={{ marginTop:"12px", padding:"9px 18px", background:"#D97706", border:"none", borderRadius:"10px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"13px", fontWeight:"700", color:"#fff", cursor:"pointer" }}
                  >
                    ➕ Add First Labour
                  </button>
                </div>
              ) : (
                /* ── Labour cards with attendance link ── */
                <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
                  {labours.slice(0, 5).map((l, i) => {
                    const avatarColors = ["#D97706","#0369A1","#059669","#7C3AED","#DC2626"];
                    const c = avatarColors[l._id?.charCodeAt(0) % avatarColors.length] || "#D97706";
                    const initials = l.name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
                    return (
                      <div key={l._id} style={{ display:"flex", alignItems:"center", gap:"12px", padding:"12px 14px", background:"#FAFAF9", borderRadius:"12px", border:"1px solid #F5F5F4", transition:"background .15s" }}>
                        {/* Avatar */}
                        <div style={{ width:"38px", height:"38px", borderRadius:"50%", background:`${c}22`, border:`1.5px solid ${c}55`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"13px", fontWeight:"800", color:c, flexShrink:0 }}>
                          {initials}
                        </div>
                        {/* Info */}
                        <div style={{ flex:1, minWidth:0 }}>
                          <p style={{ fontSize:"14px", fontWeight:"700", color:"#1C1917", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{l.name}</p>
                          <p style={{ fontSize:"11px", color:"#A8A29E", marginTop:"1px" }}>₹{l.dailyRate ?? "—"}/day</p>
                        </div>
                      </div>
                    );
                  })}
                  {labours.length > 5 && (
                    <button
                      onClick={() => navigate("/carpenter/labours")}
                      style={{ width:"100%", padding:"10px", background:"#FAF7F4", border:"1.5px dashed #E7E5E4", borderRadius:"12px", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"13px", fontWeight:"700", color:"#78716C", cursor:"pointer", marginTop:"2px" }}
                    >
                      +{labours.length - 5} more labours → View All
                    </button>
                  )}
                </div>
              )}
            </div>

          </main>
        </div>
      </div>
    </>
  );
}